/**
 * SVG data-URI generators for beautiful template components.
 *
 * Each function returns a data:image/svg+xml URI that can be used as
 * the `src` of an ImageEl on the QuickDoc canvas.
 *
 * These components use gradients, rounded corners, and subtle visual
 * touches that CardEl can't achieve — giving templates a polished,
 * modern aesthetic while remaining fully draggable canvas elements.
 */

const svgUri = (svg: string) =>
  `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg.trim())}`;

/* ================================================================
   Accent stripe – full-width gradient bar at the top of each page
   ================================================================ */
export const accentStripeSvg = (w = 612, h = 5): string =>
  svgUri(`
<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}">
  <defs>
    <linearGradient id="sg" x1="0" y1="0" x2="1" y2="0">
      <stop offset="0%" stop-color="#0f172a"/>
      <stop offset="50%" stop-color="#1e3a8a"/>
      <stop offset="100%" stop-color="#3b82f6"/>
    </linearGradient>
  </defs>
  <rect width="${w}" height="${h}" fill="url(#sg)"/>
</svg>`);

/* ================================================================
   Header banner – dark gradient rounded rect with decorative circle
   ================================================================ */
export const headerBannerSvg = (w = 540, h = 66): string =>
  svgUri(`
<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}">
  <defs>
    <linearGradient id="hg" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0%" stop-color="#0f172a"/>
      <stop offset="100%" stop-color="#1e293b"/>
    </linearGradient>
    <clipPath id="hc"><rect width="${w}" height="${h}" rx="14"/></clipPath>
  </defs>
  <rect width="${w}" height="${h}" rx="14" fill="url(#hg)"/>
  <circle cx="${w + 10}" cy="-15" r="70" fill="rgba(59,130,246,0.07)" clip-path="url(#hc)"/>
  <circle cx="${w - 40}" cy="${h + 25}" r="50" fill="rgba(59,130,246,0.04)" clip-path="url(#hc)"/>
</svg>`);

/* ================================================================
   Party card – bordered card with colored left accent bar
   ================================================================ */
export const partyCardSvg = (
  w = 258,
  h = 100,
  accent = "#0f172a"
): string =>
  svgUri(`
<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}">
  <rect x="0.5" y="0.5" width="${w - 1}" height="${h - 1}" rx="12" fill="#ffffff"
        stroke="#e2e8f0" stroke-width="1"/>
  <rect width="4" height="${h}" rx="2" fill="${accent}"/>
  <rect x="0" y="0" width="${w}" height="${h}" rx="12" fill="none"
        stroke="#e2e8f0" stroke-width="0.5" opacity="0.5"/>
</svg>`);

/* ================================================================
   Full-width party card (for offer letters, single-recipient)
   ================================================================ */
export const fullPartyCardSvg = (w = 540, h = 80): string =>
  svgUri(`
<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}">
  <rect x="0.5" y="0.5" width="${w - 1}" height="${h - 1}" rx="12" fill="#ffffff"
        stroke="#e2e8f0" stroke-width="1"/>
  <rect width="4" height="${h}" rx="2" fill="#0f172a"/>
</svg>`);

/* ================================================================
   Section number circle
   ================================================================ */
export const sectionCircleSvg = (n: number, size = 22): string =>
  svgUri(`
<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}">
  <circle cx="${size / 2}" cy="${size / 2}" r="${size / 2}" fill="#0f172a"/>
  <text x="${size / 2}" y="${size / 2}" text-anchor="middle"
        dominant-baseline="central"
        font-family="Inter, system-ui, sans-serif" font-size="11"
        font-weight="800" fill="#ffffff">${n}</text>
</svg>`);

/* ================================================================
   Divider line – thin separator
   ================================================================ */
export const dividerSvg = (w = 540, h = 1): string =>
  svgUri(`
<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}">
  <rect width="${w}" height="${h}" fill="#e2e8f0"/>
</svg>`);

/* ================================================================
   Accent divider – bold gradient separator
   ================================================================ */
export const accentDividerSvg = (w = 540, h = 2): string =>
  svgUri(`
<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}">
  <defs>
    <linearGradient id="dg" x1="0" y1="0" x2="1" y2="0">
      <stop offset="0%" stop-color="#0f172a"/>
      <stop offset="100%" stop-color="#1e3a8a"/>
    </linearGradient>
  </defs>
  <rect width="${w}" height="${h}" fill="url(#dg)"/>
</svg>`);

/* ================================================================
   Signature block frame – card with dashed lines
   ================================================================ */
export const sigBlockSvg = (w = 256, h = 200): string =>
  svgUri(`
<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}">
  <rect x="0.5" y="0.5" width="${w - 1}" height="${h - 1}" rx="12" fill="#f8fafc"
        stroke="#e2e8f0" stroke-width="1"/>
  <line x1="14" y1="30" x2="${w - 14}" y2="30" stroke="#0f172a" stroke-width="1.5"/>
  <line x1="14" y1="105" x2="${w - 14}" y2="105" stroke="#cbd5e1" stroke-width="1"
        stroke-dasharray="4 3"/>
  <line x1="14" y1="140" x2="${w - 14}" y2="140" stroke="#cbd5e1" stroke-width="1"
        stroke-dasharray="4 3"/>
  <line x1="14" y1="175" x2="${w - 14}" y2="175" stroke="#cbd5e1" stroke-width="1"
        stroke-dasharray="4 3"/>
</svg>`);

/* ================================================================
   Meta pill – rounded metadata badge
   ================================================================ */
export const metaPillSvg = (w = 180, h = 26): string =>
  svgUri(`
<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}">
  <rect x="0.5" y="0.5" width="${w - 1}" height="${h - 1}" rx="${h / 2}" fill="#f1f5f9"
        stroke="#e2e8f0" stroke-width="1"/>
  <circle cx="12" cy="${h / 2}" r="2.5" fill="#3b82f6"/>
</svg>`);

/* ================================================================
   Invoice table header – dark rounded-top bar
   ================================================================ */
export const invoiceTableHeaderSvg = (w = 540, h = 28): string =>
  svgUri(`
<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}">
  <rect width="${w}" height="${h}" rx="6" fill="#0f172a"/>
  <rect y="${h - 6}" width="${w}" height="6" fill="#0f172a"/>
</svg>`);

/* ================================================================
   Invoice table body – bordered container
   ================================================================ */
export const invoiceTableBodySvg = (w = 540, h = 140): string =>
  svgUri(`
<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}">
  <rect x="0.5" y="0.5" width="${w - 1}" height="${h - 1}" fill="#ffffff"
        stroke="#e2e8f0" stroke-width="1"/>
  <rect x="0" y="${h - 1}" width="${w}" height="1" rx="0" fill="#e2e8f0"/>
  <line x1="16" y1="46" x2="${w - 16}" y2="46" stroke="#f1f5f9" stroke-width="1"/>
  <line x1="16" y1="92" x2="${w - 16}" y2="92" stroke="#f1f5f9" stroke-width="1"/>
</svg>`);

/* ================================================================
   Invoice meta bar – three-column info strip
   ================================================================ */
export const invoiceMetaSvg = (w = 540, h = 44): string =>
  svgUri(`
<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}">
  <rect x="0.5" y="0.5" width="${w - 1}" height="${h - 1}" rx="10" fill="#f1f5f9"
        stroke="#e2e8f0" stroke-width="1"/>
  <line x1="${w / 3}" y1="6" x2="${w / 3}" y2="${h - 6}" stroke="#e2e8f0" stroke-width="1"/>
  <line x1="${(w * 2) / 3}" y1="6" x2="${(w * 2) / 3}" y2="${h - 6}" stroke="#e2e8f0" stroke-width="1"/>
</svg>`);

/* ================================================================
   Totals box
   ================================================================ */
export const totalsBoxSvg = (w = 220, h = 100): string =>
  svgUri(`
<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}">
  <rect x="0.5" y="0.5" width="${w - 1}" height="${h - 1}" rx="12" fill="#f8fafc"
        stroke="#e2e8f0" stroke-width="1"/>
  <line x1="14" y1="60" x2="${w - 14}" y2="60" stroke="#0f172a" stroke-width="1.5"/>
</svg>`);

/* ================================================================
   Note/info box
   ================================================================ */
export const noteBoxSvg = (w = 540, h = 60): string =>
  svgUri(`
<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}">
  <rect x="0.5" y="0.5" width="${w - 1}" height="${h - 1}" rx="12" fill="#f8fafc"
        stroke="#e2e8f0" stroke-width="1"/>
</svg>`);

/* ================================================================
   Logo placeholder
   ================================================================ */
export const logoPlaceholderSvg = (w = 140, h = 36): string =>
  svgUri(`
<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}">
  <rect x="1" y="1" width="${w - 2}" height="${h - 2}" rx="8" fill="#f1f5f9"
        stroke="#cbd5e1" stroke-width="1.5" stroke-dasharray="4 3"/>
  <path d="M36 22l8-8 5 6 3-3 7 5" fill="none" stroke="#94a3b8"
        stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
  <circle cx="40" cy="15" r="2.5" fill="#94a3b8"/>
  <text x="${w / 2 + 14}" y="21" text-anchor="middle" font-family="Inter, system-ui, sans-serif"
        font-size="10" font-weight="600" fill="#94a3b8">Your Logo</text>
</svg>`);

/* ================================================================
   Exports for the "elements popout" component library
   Each entry describes a component users can insert.
   ================================================================ */
export interface TemplateComponent {
  key: string;
  label: string;
  category: string;
  src: string;
  width: number;
  height: number;
}

export const TEMPLATE_COMPONENTS: TemplateComponent[] = [
  { key: "accentStripe", label: "Accent Stripe", category: "Decorative", src: accentStripeSvg(), width: 612, height: 5 },
  { key: "headerBanner", label: "Header Banner", category: "Headers", src: headerBannerSvg(), width: 540, height: 66 },
  { key: "partyCard", label: "Party Card", category: "Cards", src: partyCardSvg(), width: 258, height: 100 },
  { key: "partyCardBlue", label: "Party Card (Blue)", category: "Cards", src: partyCardSvg(258, 100, "#3b82f6"), width: 258, height: 100 },
  { key: "fullPartyCard", label: "Full-Width Card", category: "Cards", src: fullPartyCardSvg(), width: 540, height: 80 },
  { key: "divider", label: "Divider", category: "Decorative", src: dividerSvg(), width: 540, height: 1 },
  { key: "accentDivider", label: "Accent Divider", category: "Decorative", src: accentDividerSvg(), width: 540, height: 2 },
  { key: "sigBlock", label: "Signature Block", category: "Signatures", src: sigBlockSvg(), width: 256, height: 200 },
  { key: "metaPill", label: "Meta Badge", category: "Decorative", src: metaPillSvg(), width: 180, height: 26 },
  { key: "noteBox", label: "Note Box", category: "Cards", src: noteBoxSvg(), width: 540, height: 60 },
  { key: "totalsBox", label: "Totals Box", category: "Cards", src: totalsBoxSvg(), width: 220, height: 100 },
  { key: "logoPlaceholder", label: "Logo Placeholder", category: "Headers", src: logoPlaceholderSvg(), width: 140, height: 36 },
];
